/**
 * Cell helpers shared by the per-table column definitions.
 *
 * Every table repeated the same clipped-text cell and the same select-all
 * checkbox column; both live here so a fix lands everywhere at once.
 */

import { Checkbox, Tooltip, Typography } from '@mui/material';
import type { RowData } from '@tanstack/react-table';
import type { ColumnDef } from './tableFeatures';
import { fontFamilyMono } from '../../theme/theme';

/** Long value clipped to `width` px, full text on hover. CSS adds the ellipsis
 *  only when the text really overflows, so it adapts to font size and zoom. */
export const clipped = (
  value: string | undefined | null,
  width: number,
  { mono, error }: { mono?: boolean; error?: boolean } = {}
) => {
  if (!value) return '-';
  return (
    <Tooltip title={value}>
      <Typography
        variant="body2"
        noWrap
        color={error ? 'error' : undefined}
        sx={{ maxWidth: width, ...(mono && { fontFamily: fontFamilyMono, fontSize: '0.75rem' }) }}
      >
        {value}
      </Typography>
    </Tooltip>
  );
};

/** Human-readable byte size: B under 1 KB, KB under 1 MB, MB above. */
export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Compact duration: 45s, 7m, 1h 6m, 1d 1h. */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
};

/** Absolute timestamp, or `absent` when there is none. */
export const dateTime = (value: string | null | undefined, absent = '-') =>
  value ? new Date(value).toLocaleString() : absent;

/** Date without the time, for columns where the hour adds nothing. */
export const shortDate = (value: string | null | undefined, absent = '-') =>
  value ? new Date(value).toLocaleDateString() : absent;

/** Sortable accessor for a timestamp column: epoch ms, 0 when absent, so rows
 *  without a date sort to one end instead of comparing as strings. */
export const dateSortValue = (value: string | null | undefined) =>
  value ? new Date(value).getTime() : 0;

/** Placeholder for an actions cell on a row that offers none. */
export const noActions = () => (
  <Typography variant="caption" sx={{
    color: 'text.secondary'
  }}>
    No actions available
  </Typography>
);

/**
 * The row-selection checkbox column. Sticky-left so it stays visible while the
 * table scrolls sideways.
 *
 * `rowLabel` names each row's checkbox for screen readers — a plain aria-label
 * lands on MUI's roleless wrapper span and leaves the real input nameless, so
 * both checkboxes set it through `slotProps.input`.
 */
export function selectColumn<T extends RowData>(
  rowLabel: (row: T) => string,
  selectAllLabel = 'Select all rows'
): ColumnDef<T> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        size="small"
        sx={{ p: 0.5 }}
        checked={table.getIsAllRowsSelected()}
        // v9's getIsSomeRowsSelected() stays true when every row is selected.
        indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        slotProps={{ input: { 'aria-label': selectAllLabel } }}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        size="small"
        sx={{ p: 0.5 }}
        checked={row.getIsSelected()}
        // Honours a table-level `enableRowSelection` predicate; always true
        // for tables that do not set one.
        disabled={!row.getCanSelect()}
        onChange={row.getToggleSelectedHandler()}
        slotProps={{ input: { 'aria-label': `Select ${rowLabel(row.original)}` } }}
      />
    ),
    size: 44,
    enableSorting: false,
    meta: { sticky: 'left' },
  };
}
